// src/pages/FAQ.jsx
import React, { useState } from "react";
import { HelpCircle, CreditCard, Truck, Package } from "lucide-react";
import { useEffect } from "react";

const faqCategories = {
General: [
  {
    question: "What is HD Labs?",
    answer:
      "HD Labs is a Canadian online research supply company focused on providing high-quality research compounds with an emphasis on transparency, reliability, and professional service.",
  },
  {
    question: "Where is HD Labs located?",
    answer:
      "HD Labs operates within Canada and fulfills all orders domestically to ensure fast and reliable delivery.",
  },
  {
    question: "Are your products tested?",
    answer:
      "We prioritize quality and consistency in all products we offer. Product specifications and batch information are provided where applicable to maintain transparency.",
  },
  {
    question: "How can I contact HD Labs?",
    answer:
      "You can reach us through the contact page on our website. We aim to respond to all inquiries in a timely and professional manner.",
  },
  {
    question: "Do you have a physical storefront?",
    answer:
      "No. HD Labs operates exclusively online to maintain operational efficiency and competitive pricing.",
  },
],

Payments: [
  {
    question: "What currency are your prices listed in?",
    answer:
      "All prices on our website are listed in Canadian Dollars (CAD).",
  },
  {
    question: "What payment method do you accept?",
    answer:
      "We currently accept Interac e-Transfer as our sole method of payment. Payment instructions will be provided after checkout.",
  },
  {
    question: "How do I receive payment instructions?",
    answer:
      "Once your order has been placed, a confirmation email will be sent to the address provided at checkout. This email contains detailed instructions for completing your Interac e-Transfer.",
  },
  {
    question: "Do you accept PayPal or credit cards?",
    answer:
      "No. At this time, we only accept Interac e-Transfer and do not process PayPal or credit card payments.",
  },
],

Shipping: [
  {
    question: "How are orders shipped?",
    answer:
      "Orders are shipped through Canada Post. Delivery typically takes 4–6 business days depending on your location.",
  },
  {
    question: "What are your shipping rates?",
    answer:
      "Shipping is a flat rate of $20. Orders over $250 qualify for complimentary shipping.",
  },
  {
    question: "Do you ship to PO boxes?",
    answer:
      "Yes, PO box addresses within Canada are supported.",
  },
  {
    question: "Will I receive tracking information?",
    answer:
      "Yes. Once your order has been dispatched, you will receive a confirmation email containing your tracking details.",
  },
  {
    question: "Why is my tracking number not updating?",
    answer:
      "Tracking information may take time to appear in the carrier’s system. Updates sometimes begin once the parcel reaches a sorting facility. Please allow additional time for activation.",
  },
  {
    question: "Is there any customs risk?",
    answer:
      "All shipments are fulfilled within Canada and do not pass through customs. In rare cases involving carrier delays or transit issues, resolution is subject to the carrier’s policies.",
  },
  {
    question: "What is your processing time?",
    answer:
      "Orders are typically processed and dispatched within 1–2 business days after payment confirmation.",
  },
  {
    question: "What happens if a package is marked delivered but I did not receive it?",
    answer:
      "If a package is marked as delivered by the carrier, it is considered delivered. Please ensure your shipping information is accurate at checkout. Lost or misdelivered parcels are uncommon but fall under carrier responsibility.",
  },
],

Orders: [
  {
    question: "How can I check the status of my order?",
    answer:
      'You can view your order status at any time by logging into your account and navigating to the "Orders" section.',
  },
  {
    question: "Will I receive updates about my order?",
    answer:
      'Yes. You will automatically receive email notifications when your payment is confirmed and when your order has been shipped, including tracking information.',
  },
  {
    question:
      "I made a mistake on my order or selected the wrong payment method. What should I do?",
    answer:
      "Please place a new order with the correct details and complete payment for that order. Any unpaid order will automatically expire and be cancelled after 6 days.",
  },
  {
    question: "Can I delete an order after placing it?",
    answer:
      "Orders cannot be manually deleted. Unpaid orders will automatically expire after 6 days and will no longer appear in your account.",
  },
  {
    question: "Why is my tracking number not appearing online?",
    answer:
      "Tracking information may take time to activate within the carrier’s system and may not appear until the following business day. Please allow time for updates.",
  },
],
};

export default function FAQ() {
  useEffect(() => {
  document.title = "FAQ | HD Labs";
}, []);

  const [activeTab, setActiveTab] = useState("General");
  const [openIndex, setOpenIndex] = useState(null);

  const tabs = [
    { name: "General", icon: <HelpCircle size={16} /> },
    { name: "Payments", icon: <CreditCard size={16} /> },
    { name: "Shipping", icon: <Truck size={16} /> },
    { name: "Orders", icon: <Package size={16} /> },
  ];

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-white px-6 py-16">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-semibold mb-10 text-center">
          FAQ
        </h1>

        {/* Tabs */}
        <div className="flex justify-center gap-6 border-b mb-10">
          {tabs.map((tab) => (
            <button
              key={tab.name}
              onClick={() => {
                setActiveTab(tab.name);
                setOpenIndex(null);
              }}
              className={`flex items-center gap-2 pb-4 text-base font-medium transition ${
                activeTab === tab.name
                  ? "border-b-2 border-blue-700 text-blue-700"
                  : "text-gray-500 hover:text-black"
              }`}
            >
              {tab.icon}
              {tab.name}
            </button>
          ))}
        </div>

        {/* Questions */}
        <div className="space-y-4">
          {faqCategories[activeTab].map((item, index) => (
            <div key={index} className="border-b pb-4">
              <button
                onClick={() => toggleQuestion(index)}
                className="w-full text-left text-lg font-medium flex justify-between items-center py-2"
              >
                {item.question}
                <span>{openIndex === index ? "-" : "+"}</span>
              </button>
              <div
  className={`grid transition-all duration-300 ease-in-out ${
    openIndex === index
      ? "grid-rows-[1fr] opacity-100 mt-3"
      : "grid-rows-[0fr] opacity-0"
  }`}
>
  <div className="overflow-hidden">
    <p className="text-gray-600 text-sm leading-relaxed">
      {item.answer}
    </p>
  </div>
</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}